import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle2, Trash2, Clock, Ticket, X, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../context/AuthContext";
import { useLeague } from "../../context/LeagueContext";
import { fmtDate, fmtOdds, fmtTokens } from "../../lib/format";
import CouponReceipt from "../../components/CouponReceipt";

// Which pick values belong to which outcome shape
const is1X2 = (market) => market.outcomes.some((o) => o.pick);
const SPORT_GROUP_LABELS = {
  soccer: "Fotboll",
  icehockey: "Ishockey",
  basketball: "Basket",
  tennis: "Tennis",
  manual: "Manuell",
};
const isTotals = (market) => market.outcomes.some((o) => o.over !== undefined);
const isHandicap = (market) => market.outcomes.some((o) => o.home !== undefined);
// Bet365 special markets: outcomes have pick+label (Yes/No, 1X/X2/12, 1/2) - render as 1X2-style buttons
const isBet365Special = (market) => market.key.startsWith("b365_") && market.outcomes.some((o) => o.pick);

const betId = (b) => `${b.matchId}|${b.marketKey}|${b.pick}|${b.line || ""}`;

export default function Matches() {
  const { currentLeague } = useLeague();
  const [matches, setMatches] = useState([]);
  const [sports, setSports] = useState([]);
  const [players, setPlayers] = useState([]);
  const [sportFilter, setSportFilter] = useState("all");
  const [liveFilter, setLiveFilter] = useState("upcoming");
  const [selectedBets, setSelectedBets] = useState([]);
  const [stake, setStake] = useState("100");
  const [playerId, setPlayerId] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedMatch, setExpandedMatch] = useState(null);
  const [expandedMarket, setExpandedMarket] = useState(null);

  const load = () => {
    if (currentLeague) {
      const params = liveFilter === "live" ? "?live_only=true" : "";
      api.get(`/leagues/${currentLeague.id}/matches${params}`).then((r) => setMatches(r.data)).catch(() => {});
      api.get(`/leagues/${currentLeague.id}/players`).then((r) => setPlayers(r.data)).catch(() => {});
    }
  };
  useEffect(() => { load(); }, [currentLeague, liveFilter]);

  useEffect(() => {
    api.get("/sports").then((r) => setSports(r.data)).catch(() => {});
  }, []);

  const filteredMatches = matches
    .filter((m) => sportFilter === "all" || m.sport_key === sportFilter)
    .filter((m) => {
      if (!searchQuery) return true;
      const q = searchQuery.toLowerCase();
      return m.home_team.toLowerCase().includes(q) || m.away_team.toLowerCase().includes(q);
    });

  const toggleBet = (match, market, pick, line, price, selectionLabel) => {
    const id = `${match.id}|${market.key}|${pick}|${line || ""}`;
    setSelectedBets((prev) => {
      if (prev.some((b) => betId(b) === id)) return prev.filter((b) => betId(b) !== id);
      // Only one pick per market per match (can't take both over and under on same line etc.)
      const filtered = prev.filter((b) => !(b.matchId === match.id && b.marketKey === market.key));
      return [...filtered, {
        id,
        matchId: match.id,
        home_team: match.home_team,
        away_team: match.away_team,
        marketKey: market.key,
        marketLabel: market.label,
        pick,
        line: line || null,
        odds: price,
        label: selectionLabel,
      }];
    });
  };

  const removeBet = (id) => setSelectedBets((prev) => prev.filter((b) => betId(b) !== id));

  const totalOdds = useMemo(() => selectedBets.reduce((acc, b) => acc * b.odds, 1), [selectedBets]);
  const potential = (parseFloat(stake) || 0) * totalOdds;
  const selectedPlayer = players.find((p) => p.id === playerId);

  const openConfirm = () => {
    if (!selectedBets.length) return toast.error("Välj minst ett spel");
    setConfirmOpen(true);
  };

  const confirmSubmit = async () => {
    if (!playerId) return toast.error("Välj vilken spelare kupongen ska läggas på");
    setLoading(true);
    try {
      const res = await api.post(`/leagues/${currentLeague.id}/coupons`, {
        player_id: playerId,
        stake: parseFloat(stake),
        legs: selectedBets.map((b) => ({ match_id: b.matchId, market_key: b.marketKey, pick: b.pick, line: b.line })),
      });
      toast.success(`Kupongen är lagd på ${selectedPlayer?.name}!`);
      setSelectedBets([]);
      setPlayerId("");
      setConfirmOpen(false);
      setReceipt(res.data);
    } catch (err) {
      toast.error(formatApiError(err, "Kunde inte lägga kupongen"));
    } finally {
      setLoading(false);
    }
  };

  const settle = async (id) => {
    try {
      const res = await api.post(`/leagues/${currentLeague.id}/matches/${id}/settle`);
      toast.success(`Match avgjord — resultat: ${res.data.result}`);
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Kunde inte avgöra matchen"));
    }
  };

  const remove = async (id) => {
    await api.delete(`/leagues/${currentLeague.id}/matches/${id}`).catch(() => {});
    toast.success("Matchen borttagen");
    load();
  };

  const mainMarket = (m) => m.markets?.find((mk) => is1X2(mk)) || m.markets?.[0];
  const otherMarkets = (m) => m.markets?.filter((mk) => mk !== mainMarket(m)) || [];

  const renderMarket = (m, market, compact = false) => {
    if (is1X2(market)) return render1X2(m, market, compact);
    if (isTotals(market)) return renderTotals(m, market);
    return renderHandicap(m, market);
  };

  const render1X2 = (m, market, compact = false) => (
    <div className="flex gap-2" data-testid={`market-${m.id}-${market.key}`}>
      {market.outcomes.map((o) => {
        const id = `${m.id}|${market.key}|${o.pick}|`;
        const isSel = selectedBets.some((b) => betId(b) === id);
        return (
          <button
            key={o.pick}
            onClick={(e) => { e.stopPropagation(); toggleBet(m, market, o.pick, null, o.price, o.label); }}
            data-testid={`bet-${m.id}-${market.key}-${o.pick}`}
            className={`flex-1 min-w-[56px] rounded-xl border px-3 py-2 text-center transition-colors ${
              isSel ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 hover:border-blue-300 text-slate-900"
            }`}
          >
            <p className={`text-[10px] font-semibold ${isSel ? "text-blue-100" : "text-slate-500"}`}>{o.pick}</p>
            {!compact && <p className={`text-[10px] truncate ${isSel ? "text-blue-100" : "text-slate-400"}`}>{o.label}</p>}
            <p className="font-mono2 font-semibold text-sm">{fmtOdds(o.price)}</p>
          </button>
        );
      })}
    </div>
  );

  const renderTotals = (m, market) => (
    <div className="rounded-xl border border-slate-200 divide-y divide-slate-200 overflow-hidden" data-testid={`market-${m.id}-${market.key}`}>
      {market.outcomes.map((o) => (
        <div key={o.line} className="flex gap-2 items-center bg-slate-50/60 px-3 py-2">
          <span className="text-xs font-semibold text-slate-700 w-16 shrink-0 font-mono2 bg-slate-200/70 rounded-md px-2 py-0.5 text-center">{o.line}</span>
          {[["over", "Över", o.over], ["under", "Under", o.under]].map(([pick, lbl, price]) => {
            const id = `${m.id}|${market.key}|${pick}|${o.line}`;
            const isSel = selectedBets.some((b) => betId(b) === id);
            return (
              <button
                key={pick}
                onClick={() => toggleBet(m, market, pick, o.line, price, `${lbl} ${o.line}`)}
                data-testid={`bet-${m.id}-${market.key}-${pick}-${o.line}`}
                className={`flex-1 rounded-xl border px-3 py-2 text-center transition-colors ${
                  isSel ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 hover:border-blue-300 text-slate-900"
                }`}
              >
                <p className={`text-[10px] font-semibold ${isSel ? "text-blue-100" : "text-slate-500"}`}>{lbl}</p>
                <p className="font-mono2 font-semibold text-sm">{fmtOdds(price)}</p>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );

  const renderHandicap = (m, market) => (
    <div className="rounded-xl border border-slate-200 divide-y divide-slate-200 overflow-hidden" data-testid={`market-${m.id}-${market.key}`}>
      {market.outcomes.map((o) => (
        <div key={o.line} className="flex gap-2 items-center bg-slate-50/60 px-3 py-2">
          <span className="text-xs font-semibold text-slate-700 w-16 shrink-0 font-mono2 bg-slate-200/70 rounded-md px-2 py-0.5 text-center">{o.line}</span>
          {[["home", m.home_team, o.home], ["away", m.away_team, o.away]].map(([pick, team, price]) => {
            const side = pick === "home"
              ? o.line
              : o.line.split(",").map((p) => {
                  const v = -parseFloat(p);
                  return Number.isInteger(v) ? String(v) : String(v);
                }).join(",");
            const firstVal = parseFloat(String(side).split(",")[0]);
            const sideText = firstVal > 0 ? `+${side}` : side;
            const id = `${m.id}|${market.key}|${pick}|${o.line}`;
            const isSel = selectedBets.some((b) => betId(b) === id);
            return (
              <button
                key={pick}
                onClick={() => toggleBet(m, market, pick, o.line, price, `${team} (${sideText})`)}
                data-testid={`bet-${m.id}-${market.key}-${pick}-${o.line}`}
                className={`flex-1 rounded-xl border px-3 py-2 text-center transition-colors ${
                  isSel ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-slate-200 hover:border-blue-300 text-slate-900"
                }`}
              >
                <p className={`text-[10px] font-semibold truncate ${isSel ? "text-blue-100" : "text-slate-500"}`}>{team} {sideText}</p>
                <p className="font-mono2 font-semibold text-sm">{fmtOdds(price)}</p>
              </button>
            );
          })}
        </div>
      ))}
    </div>
  );

  return (
    <div data-testid="admin-matches">
      <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight mb-2">Matcher</h1>
      <p className="text-sm text-slate-500 mb-6">Riktiga matcher med riktiga odds för {currentLeague?.name}.</p>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-8">
        <button
          onClick={() => setLiveFilter("upcoming")}
          className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
            liveFilter === "upcoming" ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
          data-testid="filter-upcoming"
        >
          Kommande
        </button>
        <button
          onClick={() => setLiveFilter("live")}
          className={`px-5 py-2.5 rounded-full text-sm font-medium transition-colors ${
            liveFilter === "live" ? "bg-red-600 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
          data-testid="filter-live"
        >
          🔴 Live nu
        </button>
        <select
          value={sportFilter}
          onChange={(e) => setSportFilter(e.target.value)}
          className="bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
          data-testid="sport-filter"
        >
          <option value="all">Alla sporter</option>
          {sports.map((s) => (
            <option key={s.key} value={s.key}>{s.name}</option>
          ))}
        </select>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Sök match..."
          className="flex-1 min-w-[200px] bg-white border border-slate-200 rounded-xl px-4 py-2 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
          data-testid="match-search-input"
        />
      </div>

      {/* Betting slip */}
      <AnimatePresence>
        {selectedBets.length > 0 && (
          <motion.div
            initial={{ opacity: 0, x: 400 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 400 }}
            transition={{ type: "spring", damping: 30, stiffness: 300 }}
            className="fixed right-0 top-0 bottom-0 w-full sm:w-96 bg-white border-l-2 border-slate-300 shadow-2xl overflow-y-auto z-40 p-6"
            data-testid="betting-slip"
          >
            <div className="flex items-center justify-between mb-5">
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">Din kupong ({selectedBets.length} spel)</p>
              <button onClick={() => setSelectedBets([])} className="text-slate-400 hover:text-red-600 transition-colors" data-testid="clear-slip">
                <X size={18} />
              </button>
            </div>

            <div className="space-y-2 mb-6">
              {selectedBets.map((bet) => (
                <div key={bet.id} className="bg-[#FAFAFC] border border-slate-200 rounded-xl px-4 py-2.5">
                  <div className="flex items-center justify-between gap-2 mb-1">
                    <div className="flex-1">
                      <div className="flex items-center justify-between text-sm">
                        <span>{bet.home_team} vs {bet.away_team}</span>
                        <span className="font-mono2 font-semibold">{bet.label} · {fmtOdds(bet.odds)}</span>
                      </div>
                      <p className="text-[10px] text-slate-500 uppercase tracking-wide mt-1">{bet.marketLabel}</p>
                    </div>
                    <button
                      onClick={() => removeBet(bet.id)}
                      className="text-slate-400 hover:text-red-600 transition-colors p-1"
                      data-testid={`remove-${bet.id}`}
                    >
                      <X size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex flex-col gap-3 bg-slate-900 rounded-xl px-4 py-3">
              <div className="flex items-end gap-4">
                <div>
                  <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-400 block mb-0.5">Insats</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={stake}
                    onChange={(e) => setStake(e.target.value)}
                    data-testid="bet-stake-input"
                    className="w-20 bg-slate-800 border border-slate-700 text-white rounded-lg px-2 py-1 text-xs outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 transition-colors font-mono2"
                  />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Odds</p>
                  <p className="font-mono2 font-bold text-white text-sm" data-testid="bet-total-odds">{fmtOdds(totalOdds)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-widest font-semibold text-slate-400">Vinst</p>
                  <p className="font-mono2 font-bold text-blue-400 text-sm" data-testid="bet-potential">{fmtTokens(potential)}</p>
                </div>
              </div>
              <button
                onClick={openConfirm}
                disabled={loading || !players.length}
                data-testid="place-bet-button"
                className="w-full inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 active:scale-[0.98] disabled:opacity-50 text-white font-semibold px-4 py-2 rounded-lg transition-colors text-sm"
              >
                <Ticket size={14} /> Lägg kupong
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <CouponReceipt coupon={receipt} onClose={() => setReceipt(null)} />

      {/* Confirm dialog */}
      <AnimatePresence>
        {confirmOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm"
            onClick={() => !loading && setConfirmOpen(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: 16 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-3xl p-7 sm:p-8 shadow-2xl"
            >
              <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 mb-5">Bekräfta kupong</p>

              <div className="mb-6">
                <label className="text-[11px] text-slate-500 block mb-1.5">Välj spelare som kupongen läggs på</label>
                <select
                  value={playerId}
                  onChange={(e) => setPlayerId(e.target.value)}
                  data-testid="coupon-player-select"
                  className="w-full bg-[#FAFAFC] border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-colors"
                >
                  <option value="" disabled>Välj spelare...</option>
                  {players.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
                {selectedPlayer && (
                  <div className="mt-3 bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-display font-bold shrink-0">
                      {selectedPlayer.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-widest font-semibold text-blue-600">Kupongen läggs på</p>
                      <p className="font-display font-bold text-lg text-slate-900">{selectedPlayer.name}</p>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between gap-6 bg-slate-900 rounded-2xl px-5 py-4 mb-7 text-sm">
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-400">Insats</p>
                  <p className="font-mono2 font-bold text-white">{fmtTokens(parseFloat(stake) || 0)} tokens</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-400">Totalodds</p>
                  <p className="font-mono2 font-bold text-white">{fmtOdds(totalOdds)}</p>
                </div>
                <div>
                  <p className="text-[11px] uppercase tracking-widest font-semibold text-slate-400">Möjlig vinst</p>
                  <p className="font-mono2 font-bold text-blue-400">{fmtTokens(potential)} tokens</p>
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setConfirmOpen(false)}
                  disabled={loading}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold px-5 py-3 rounded-xl transition-colors border border-slate-200"
                >
                  Ändra
                </button>
                <button
                  onClick={confirmSubmit}
                  disabled={loading || !playerId}
                  data-testid="confirm-bet-button"
                  className="flex-1 inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-semibold px-5 py-3 rounded-xl transition-colors"
                >
                  <Ticket size={16} /> {loading ? "Lägger..." : selectedPlayer ? `Lägg på ${selectedPlayer.name}` : "Lägg kupong"}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Match list */}
      <div className={`space-y-4 transition-all duration-300 ${selectedBets.length > 0 ? "sm:mr-96" : ""}`}>
        {filteredMatches.length === 0 && <p className="text-sm text-slate-500">Inga matcher hittades.</p>}
        {filteredMatches.map((m, i) => {
          const main = mainMarket(m);
          const others = otherMarkets(m);
          const isOpen = expandedMatch === m.id;
          return (
            <motion.div
              key={m.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.04, 0.4) }}
              className="bg-white border border-slate-200 rounded-2xl overflow-hidden"
              data-testid={`match-${m.id}`}
            >
              <div
                className="w-full p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-5 hover:bg-slate-50 transition-colors text-left cursor-pointer"
                onClick={() => { setExpandedMatch(isOpen ? null : m.id); setExpandedMarket(null); }}
              >
                <div className="flex-1">
                  <p className="font-display font-semibold text-lg">{m.home_team} <span className="text-slate-400 font-normal">mot</span> {m.away_team}</p>
                  <p className="text-xs text-slate-500 mt-1 flex items-center gap-1.5 flex-wrap">
                    <Clock size={12} /> {fmtDate(m.start_time)}
                    <span className="text-slate-400">· {m.league_name} <span className="text-slate-400/70">({SPORT_GROUP_LABELS[m.group] || m.group})</span></span>
                    {m.status === "live" && (
                      <span className="ml-1 inline-flex items-center gap-1 bg-red-50 text-red-700 border border-red-200 px-2 py-0.5 rounded-md font-semibold">🔴 LIVE</span>
                    )}
                    {m.status === "settled" && (
                      <span className="ml-1 inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-md font-semibold">Avgjord: {m.result}</span>
                    )}
                  </p>
                </div>
                {main && (
                  <div className="w-full lg:w-72" onClick={(e) => e.stopPropagation()}>
                    {renderMarket(m, main, true)}
                  </div>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setExpandedMatch(isOpen ? null : m.id); setExpandedMarket(null); }}
                  className="text-slate-400 hover:text-slate-600 transition-colors self-end lg:self-center"
                  data-testid={`expand-${m.id}`}
                  aria-label={isOpen ? "Stäng" : "Expandera"}
                >
                  {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>

              {isOpen && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  className="border-t border-slate-200 p-5 sm:p-6 space-y-5"
                >
                  {others.map((market) => {
                    const openKey = `${m.id}|${market.key}`;
                    const isMarketOpen = expandedMarket === openKey;
                    return (
                      <div key={market.key} className="border-t border-slate-100 pt-4">
                        <button
                          onClick={() => setExpandedMarket(isMarketOpen ? null : openKey)}
                          className="flex items-center justify-between w-full mb-3 group"
                          data-testid={`toggle-market-${m.id}-${market.key}`}
                        >
                          <p className="text-xs uppercase tracking-widest font-semibold text-slate-500 group-hover:text-slate-700 transition-colors">{market.label}</p>
                          {isMarketOpen ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
                        </button>
                        {isMarketOpen && (
                          <div className="bg-white rounded-xl shadow-sm ring-1 ring-slate-900/5 p-2">
                            {isBet365Special(market) ? render1X2(m, market) : is1X2(market) ? render1X2(m, market) : isTotals(market) ? renderTotals(m, market) : renderHandicap(m, market)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {m.source === "manual" && m.status === "upcoming" && (
                    <div className="flex gap-2 pt-2 border-t border-slate-100">
                      <button
                        onClick={() => settle(m.id)}
                        className="inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
                        data-testid={`settle-${m.id}`}
                      >
                        <CheckCircle2 size={15} /> Avgör match
                      </button>
                      <button
                        onClick={() => remove(m.id)}
                        className="inline-flex items-center gap-1.5 text-slate-500 hover:text-red-600 border border-slate-200 hover:border-red-200 text-sm font-medium px-4 py-2.5 rounded-xl transition-colors"
                        data-testid={`delete-${m.id}`}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>
                  )}
                </motion.div>
              )}
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
